import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpDown, Play } from 'lucide-react';
import { TableFooter } from '../ui';
import { useCurrency } from '../../contexts/CurrencyContext';
import { alignmentForType, makeCellFormatter } from './lib/format';
import { buildDisplayRows, canShowSubtotals } from './lib/subtotals';
import { PAGE_SIZE_OPTIONS } from './useReportRun';

const TH = 'px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap';

function SortIcon({ dir }) {
  if (dir === 'asc') return <ArrowUp className="w-3.5 h-3.5 inline ml-1" />;
  if (dir === 'desc') return <ArrowDown className="w-3.5 h-3.5 inline ml-1" />;
  return <ArrowUpDown className="w-3.5 h-3.5 inline ml-1 text-gray-300" />;
}

// Build a link target from a column's link spec and the row's hidden id keys.
// Returns null when any id is missing, so the cell falls back to plain text.
function linkTarget(col, row) {
  if (!col.link || !col.link.route) return null;
  let href = col.link.route;
  for (const [placeholder, rowKey] of Object.entries(col.link.params || {})) {
    const value = row[rowKey];
    if (value === null || value === undefined || value === '') return null;
    href = href.replace(`{${placeholder}}`, encodeURIComponent(value));
  }
  return href;
}

function Skeleton() {
  return (
    <div className="p-4 space-y-3 animate-pulse">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="grid grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((__, j) => (
            <div key={j} className="h-4 bg-gray-200 rounded" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Generic results grid: columns and rows come straight from a RunResult.
 * Supports header-click sorting, a grand-total footer and client-side
 * subtotals when grouped by two or more dimensions.
 */
const ResultsGrid = ({
  result,
  status = 'idle',
  error = null,
  groupBy = [],
  sort = [],
  onSort,
  page = 1,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  stale = false,
  emptyHint = 'Run the report to see results',
}) => {
  const { formatPrice } = useCurrency();
  const fmt = useMemo(() => makeCellFormatter({ formatPrice }), [formatPrice]);

  const columns = result?.columns || [];
  const displayRows = useMemo(() => buildDisplayRows(result, groupBy), [result, groupBy]);
  const subtotalsHidden = result && groupBy.length >= 2 && !canShowSubtotals(result, groupBy);
  const totals = result?.totals || null;
  const sortMap = useMemo(() => Object.fromEntries((sort || []).map((s) => [s.by, s.dir])), [sort]);

  const totalRows = result?.total_rows;
  const totalPages = totalRows != null ? Math.max(1, Math.ceil(totalRows / pageSize)) : result?.has_more ? page + 1 : page;

  if (status === 'idle' && !result) {
    return (
      <div className="border-2 border-dashed border-gray-200 rounded-lg p-10 text-center text-gray-500">
        <Play className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">{emptyHint}</p>
      </div>
    );
  }
  if (status === 'error' && error) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-sm text-red-700 whitespace-pre-wrap">
        <p className="font-medium">{error.message}</p>
        {error.errors?.length > 0 && (
          <ul className="list-disc ml-5 mt-2 space-y-1">
            {error.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  if (status === 'running' && !result) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <Skeleton />
      </div>
    );
  }
  if (!result) return null;

  // The same paging bar goes above and below the grid; both drive the same page state.
  const renderPaging = (placement) =>
    onPageChange && (
      <TableFooter
        currentPage={page}
        totalPages={totalPages}
        itemsPerPage={pageSize}
        totalItems={totalRows != null ? totalRows : result.rows.length + (result.has_more ? '+' : '')}
        onPageChange={onPageChange}
        onItemsPerPageChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
        itemsPerPageOptions={pageSizeOptions}
        itemLabel="rows"
        placement={placement}
      />
    );

  return (
    <div className={`relative ${stale ? 'ring-1 ring-amber-300 rounded-lg' : ''}`}>
      {status === 'running' && (
        <div className="absolute inset-0 z-20 bg-white/60 flex items-center justify-center rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}
      {renderPaging('top')}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {columns.map((col) => {
                  const align = alignmentForType(col.type);
                  const sortable = !!onSort && col.role !== 'key';
                  return (
                    <th
                      key={col.id}
                      scope="col"
                      title={col.label}
                      onClick={sortable ? () => onSort(col.id) : undefined}
                      className={`${TH} ${align === 'right' ? 'text-right' : 'text-left'} ${sortable ? 'cursor-pointer select-none hover:text-gray-700' : ''}`}
                    >
                      {col.label}
                      {sortable && <SortIcon dir={sortMap[col.id]} />}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayRows.length === 0 && (
                <tr>
                  <td colSpan={Math.max(columns.length, 1)} className="px-4 py-10 text-center text-sm text-gray-500">
                    No rows for the selected range and filters.
                  </td>
                </tr>
              )}
              {displayRows.map((entry) =>
                entry.kind === 'subtotal' ? (
                  <tr key={entry.key} className="bg-gray-50 font-medium border-t border-gray-200">
                    {columns.map((col, ci) => {
                      const align = alignmentForType(col.type);
                      let text = '';
                      if (ci === 0) text = `Subtotal — ${fmt(entry.groupValue, col)}`;
                      else if (entry.sums[col.id] !== undefined) text = fmt(entry.sums[col.id], col);
                      return (
                        <td key={col.id} className={`px-4 py-2 text-sm text-gray-700 whitespace-nowrap ${align === 'right' ? 'text-right tabular-nums' : ''}`}>
                          {text}
                        </td>
                      );
                    })}
                  </tr>
                ) : (
                  <tr key={entry.key} className="hover:bg-gray-50">
                    {columns.map((col) => {
                      const align = alignmentForType(col.type);
                      return (
                        <td key={col.id} className={`px-4 py-2 text-sm text-gray-900 whitespace-nowrap ${align === 'right' ? 'text-right tabular-nums' : ''}`}>
                          {(() => {
                            const href = linkTarget(col, entry.row);
                            const text = fmt(entry.row[col.id], col);
                            return href ? (
                              <Link to={href} className="text-primary-600 hover:underline" onClick={(e) => e.stopPropagation()}>
                                {text}
                              </Link>
                            ) : (
                              text
                            );
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                )
              )}
            </tbody>
            {totals && displayRows.length > 0 && (
              <tfoot className="bg-gray-100 font-semibold sticky bottom-0">
                <tr>
                  {columns.map((col, ci) => {
                    const align = alignmentForType(col.type);
                    const has = totals[col.id] !== undefined && (col.totalable || col.role === 'measure');
                    const text = has ? fmt(totals[col.id], col) : ci === 0 ? 'Total' : '';
                    return (
                      <td key={col.id} className={`px-4 py-2 text-sm text-gray-900 whitespace-nowrap ${align === 'right' ? 'text-right tabular-nums' : ''}`}>
                        {text}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {subtotalsHidden && (
        <p className="mt-2 text-xs text-gray-500">Increase the page size to see group subtotals (they need the whole result on one page).</p>
      )}
      {renderPaging('bottom')}
    </div>
  );
};

export default ResultsGrid;
